import { DialogComponent } from './dialog';

/**
 * Production SizeGuideComponent
 * - Language-safe: uses metaobject handles (department/category)
 * - Shoes: vendor -> shoes -> gender
 * - Clothing/accessories: root-key tables or global clothing fallback
 */
class SizeGuideComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    this.initializeSizeGuide();
  }

  initializeSizeGuide() {
    const sizeGuideData = window.sizeGuideData;
    if (!sizeGuideData || !this.refs?.sizeGuideTable) {
      this.classList.add('hidden');
      return;
    }

    console.info('SizeGuideComponent initialized: ', sizeGuideData);

    this.sizeGuideData = sizeGuideData;

    this.productGender = window.productGender?.toLowerCase() || 'unisex';
    this.vendor = window.vendor || '';
    this.sizeGuideType = window.sizeGuideType || 'clothing';
    this.sizeOrigin = window.sizeOrigin?.toLowerCase() || 'UNI';
    this.productType = window.productType?.toLowerCase() || ''; // legacy fallback only
    this.productTypeExtension = window.productTypeExtension || '';
    this.overwriteCustomSG = window.overwriteCustomSG === true || window.overwriteCustomSG === 'true';

    this.productDepartment = window.productDepartment?.toLowerCase() || '';
    this.productCategory = window.productCategory?.toLowerCase() || '';

    this.processProductType();

    const meta = this.resolveFromMetaHandles();
    this.resolvedKey = meta ? meta.key : this.normalizeLegacyProductType(this.productType);
    if (meta) this.sizeGuideType = meta.sizeGuideType;

    const tableObj = this.resolveSizeGuideTable();
    if (!tableObj) {
      this.classList.add('hidden');
      return;
    }

    this.classList.remove('hidden');
    this.buildSizeGuide(tableObj);
  }

  processProductType() {
    if (!this.productTypeExtension) return;
    const formatted = this.productTypeExtension.charAt(0).toUpperCase() + this.productTypeExtension.slice(1).toLowerCase();
    this.productType = `${this.productType}_${formatted}`.toLowerCase();
  }

  normalizeKey(v) {
    return String(v ?? '')
      .trim()
      .toLowerCase();
  }

  findKeyCI(obj, target) {
    if (!obj) return null;
    const t = this.normalizeKey(target);
    return Object.keys(obj).find((k) => this.normalizeKey(k) === t) || null;
  }

  getGenderCandidates(gender) {
    const g = this.normalizeKey(gender);
    if (g === 'men' || g === 'mens' || g === 'men’s') return ["men's", 'unisex'];
    if (g === 'women' || g === 'womens' || g === 'women’s') return ["women's", 'unisex'];
    if (g === "men's" || g === "women's" || g === 'unisex') return [g, 'unisex'];
    return [gender, 'unisex'].map((x) => String(x));
  }

  resolveFromMetaHandles() {
    const dept = this.normalizeKey(this.productDepartment);
    const cat = this.normalizeKey(this.productCategory);

    if (!dept && !cat) return null;

    if (dept.includes('footwear') || dept.includes('shoes')) {
      return { sizeGuideType: 'shoes', key: 'shoes' };
    }

    if (dept.includes('clothing') || dept.includes('apparel')) {
      if (cat.includes('pants')) return { sizeGuideType: 'clothing', key: 'clothing_pants' };
      if (cat.includes('jeans') || cat.includes('shorts')) return { sizeGuideType: 'clothing', key: 'category.jeans' };
      return { sizeGuideType: 'clothing', key: 'department.clothing' };
    }

    if (dept.includes('jewelry') || dept.includes('accessories')) {
      if (cat.includes('rings')) return { sizeGuideType: 'clothing', key: 'accessories_rings' };
      return { sizeGuideType: 'clothing', key: 'accessories' };
    }

    return null;
  }

  normalizeLegacyProductType(productType) {
    const pt = this.normalizeKey(productType).replace(/\s+/g, '_');
    const legacyMap = {
      shoes_sneakers: 'shoes',
      shoes_sneaker: 'shoes',
      shoes: 'shoes',
      clothing: 'clothing',
      accessories: 'accessories',
    };
    return legacyMap[pt] || pt;
  }

  findInputRowKey(tableObj) {
    return Object.keys(tableObj || {}).find((k) => this.normalizeKey(k).startsWith('input_')) || null;
  }

  resolveSizeGuideTable() {
    const data = this.sizeGuideData;
    const genders = this.getGenderCandidates(this.productGender);

    // Shoes: vendor -> shoes -> gender
    if (this.sizeGuideType === 'shoes' && this.vendor && !this.overwriteCustomSG) {
      const vendorKey = this.findKeyCI(data, this.vendor);
      const vendorData = vendorKey ? data[vendorKey] : null;

      const shoesKey = this.findKeyCI(vendorData, 'shoes');
      const shoesBucket = shoesKey ? vendorData[shoesKey] : null;

      if (shoesBucket) {
        for (const g of genders) {
          const gKey = this.findKeyCI(shoesBucket, g);
          if (gKey && shoesBucket[gKey]) return shoesBucket[gKey];
        }
      }
    }

    // Vendor -> department.clothing -> gender
    if (this.vendor && this.resolvedKey) {
      const vendorKey = this.findKeyCI(data, this.vendor);
      const vendorData = vendorKey ? data[vendorKey] : null;

      const deptKey = this.findKeyCI(vendorData, this.resolvedKey);
      const deptBucket = deptKey ? vendorData[deptKey] : null;

      if (deptBucket) {
        for (const g of genders) {
          const gKey = this.findKeyCI(deptBucket, g);
          if (gKey && typeof deptBucket[gKey] === 'object') {
            return deptBucket[gKey];
          }
        }
      }
    }

    // Root key tables
    if (this.resolvedKey) {
      const keyCI = this.findKeyCI(data, this.resolvedKey);
      const root = keyCI ? data[keyCI] : null;

      if (root) {
        for (const g of genders) {
          const gKey = this.findKeyCI(root, g);
          if (!gKey) continue;

          const byGender = root[gKey];
          const originKey = this.findKeyCI(byGender, this.sizeOrigin) || this.findKeyCI(byGender, 'uni') || this.findKeyCI(byGender, 'eu') || null;

          if (originKey && typeof byGender?.[originKey] === 'object') return byGender[originKey];
          if (byGender && typeof byGender === 'object') return byGender;
        }
      }
    }

    // Global clothing fallback
    const clothingKey = this.findKeyCI(data, 'clothing');
    if (clothingKey) {
      const clothing = data[clothingKey];
      for (const g of genders) {
        const gKey = this.findKeyCI(clothing, g);
        if (!gKey) continue;

        const byGender = clothing[gKey];
        const originKey = this.findKeyCI(byGender, this.sizeOrigin) || this.findKeyCI(byGender, 'uni') || this.findKeyCI(byGender, 'eu') || null;

        if (originKey && typeof byGender?.[originKey] === 'object') return byGender[originKey];
      }
    }

    return null;
  }

  buildSizeGuide(tableObj) {
    let head = '';
    let body = '';

    const inputKey = this.findInputRowKey(tableObj);
    const inputRow = inputKey ? tableObj[inputKey] : null;

    if (Array.isArray(inputRow) && inputRow.length > 1) {
      head = '<tr>';
      if(this.resolvedKey == "category.jeans"){
        head += `<th>Size</th>`;
      }
      for (let i = 1; i < inputRow.length; i++) head += `<th>${inputRow[i] ?? ''}</th>`;
      
      head += '</tr>';
    }

    const rows = Object.entries(tableObj || {})
      .filter(([k, row]) => row && row.length && !this.normalizeKey(k).startsWith('input_'))
      .filter(([k]) => this.normalizeKey(k) !== '')
      .map(([, row]) => row)
      .filter((row) => Array.isArray(row) && row.length > 1);

    // Shoes: sort by EU column if possible
    if (this.sizeGuideType === 'shoes' && Array.isArray(inputRow)) {
      const euIndex = inputRow.findIndex((x) => this.normalizeKey(x) === 'eu');
      const idx = euIndex > -1 ? euIndex : 1;

      rows.sort((a, b) => {
        const aEU = parseFloat(String(a?.[idx] ?? '').replace(',', '.'));
        const bEU = parseFloat(String(b?.[idx] ?? '').replace(',', '.'));
        const aOk = Number.isFinite(aEU);
        const bOk = Number.isFinite(bEU);
        if (aOk && bOk) return aEU - bEU;
        return String(a?.[0] ?? '').localeCompare(String(b?.[0] ?? ''), undefined, { numeric: true });
      });
    }

    for (const row of rows) {
      body += '<tr>';
      if(this.resolvedKey == "category.jeans"){
        for (let i = 0; i < row.length; i++) body += `<td>${row[i] ?? ''}</td>`;
      }else{
        for (let i = 1; i < row.length; i++) body += `<td>${row[i] ?? ''}</td>`;
      }
      body += '</tr>';
    }

    const html = head + body;
    if (!html) {
      this.classList.add('hidden');
      return;
    }

    this.refs.sizeGuideTable.innerHTML = html;
  }

  open() {
    this.showDialog();
    this.initializeSizeGuide();
  }

  close() {
    this.closeDialog();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }
}

if (!customElements.get('size-guide')) {
  customElements.define('size-guide', SizeGuideComponent);
}
